import React, { Component } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  Box,
  Accordion,
  AccordionSummary,
  Typography,
  Grid,
} from "@mui/material";
import { formatPriceValue } from "../../../../utils/FormatPrice";
import {
  StyledTableRow,
  StyledBigTableHeader,
} from "../../../../shared/Tables/TableRow";
import fordStyles from "../../orderDetails/NAOrderDetailsNewVersion.module.scss";
import lincolnStyles from "../../orderDetails/NAOrderDetailsNewVersionLincoln.module.scss";
import { IS_FORD_BRAND } from "../../../../utils/EmpUtil";
import EMPEmptyState from "../../../../shared/components/EMPEmptyState/EMPEmptyState";
import EMPAccordion from "../../../../shared/components/EMPAccordion/EMPAccordion";

class PricingSummary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pricingSummaryExpansion: true,
      taxesAndFeesExpansion: false,
      taxesAndFees_regLicTitleFees: false,
      taxesAndFees_otherFees: false,
    };

    this.index = 0;
  }

  formatPricingDisplay = (value, displayZero = false) => {
    if (value === null || value === undefined) {
      return displayZero ? "$0.00" : <EMPEmptyState />;
    }
    return formatPriceValue(value);
  };

  componentDidUpdate(prevProps) {
    if (prevProps.expandAccordian !== this.props.expandAccordian) {
      this.setState({ pricingSummaryExpansion: this.props.expandAccordian });
    }
  }

  flipIcon() {
    this.setState({
      pricingSummaryExpansion: !this.state.pricingSummaryExpansion,
    });
  }

  expandAccordion(name) {
    this.setState({ [name]: !this.state[name] });
  }

  renderTaxesAndFees = (taxesData, label, expansionKey) => {
    if (!taxesData) {
      return null;
    }

    const styles =
      this.props.user && IS_FORD_BRAND(this.props.user.brand)
        ? fordStyles
        : lincolnStyles;

    return (
      <Accordion
        defaultExpanded={false}
        onChange={() => this.expandAccordion(expansionKey)}
        elevation={0}
      >
        <AccordionSummary>
          <div className={styles.headerBar}>
            <div>
              <Typography
                variant="h5"
                display="inline"
                sx={{ mr: 2 }}
                color="text.primary"
              >
                {label}
              </Typography>
            </div>
            <div className={styles.acceptRejectDiv}>
              <Typography variant="h5" color="text.primary">
                {formatPriceValue(taxesData.totalEstimatedTaxAndFees)}
              </Typography>
              {this.state[expansionKey] ? (
                <ExpandLessIcon data-testid="less" />
              ) : (
                <ExpandMoreIcon data-testid="more" />
              )}
            </div>
          </div>
        </AccordionSummary>

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.taxes")}
          indent
          data={formatPriceValue(taxesData.taxes)}
          index={++this.index}
        />

        {taxesData.docFeeName && taxesData.docFee && (
          <StyledTableRow
            text={taxesData.docFeeName}
            indent
            data={formatPriceValue(taxesData.docFee)}
            index={++this.index}
          />
        )}

        {taxesData.regLicTitleFees && taxesData.regLicTitleFees.length > 0 && (
          <Accordion
            onChange={() =>
              this.expandAccordion(`${expansionKey}_regLicTitleFees`)
            }
            elevation={0}
          >
            <AccordionSummary>
              <div className={styles.headerBar}>
                <Typography variant="h5" color="text.primary">
                  {this.props.t(
                    "OrderDetailsPricingSummary.regLicTitleFeesTotal"
                  )}
                </Typography>
                <div className={styles.acceptRejectDiv}>
                  <Typography variant="h5" color="text.primary">
                    {formatPriceValue(taxesData.regLicTitleFeesTotal)}
                  </Typography>
                  {this.state[`${expansionKey}_regLicTitleFees`] ? (
                    <ExpandLessIcon data-testid="less" />
                  ) : (
                    <ExpandMoreIcon data-testid="more" />
                  )}
                </div>
              </div>
            </AccordionSummary>

            {taxesData.regLicTitleFees.map((fee, index) => (
              <StyledTableRow
                key={index}
                text={fee.name}
                indent
                data={formatPriceValue(fee.amount)}
                index={++this.index}
              />
            ))}
          </Accordion>
        )}

        {taxesData.otherFees && taxesData.otherFees.length > 0 && (
          <Accordion
            onChange={() => this.expandAccordion(`${expansionKey}_otherFees`)}
            elevation={0}
          >
            <AccordionSummary>
              <div className={styles.headerBar}>
                <Typography variant="h5" color="text.primary">
                  {this.props.t("OrderDetailsPricingSummary.otherTaxesTotal")}
                </Typography>
                <div className={styles.acceptRejectDiv}>
                  <Typography variant="h5" color="text.primary">
                    {formatPriceValue(taxesData.otherTaxesTotal)}
                  </Typography>
                  {this.state[`${expansionKey}_otherFees`] ? (
                    <ExpandLessIcon data-testid="less" />
                  ) : (
                    <ExpandMoreIcon data-testid="more" />
                  )}
                </div>
              </div>
            </AccordionSummary>

            {taxesData.otherFees.map((fee, index) => (
              <StyledTableRow
                key={index}
                text={fee.name}
                indent
                data={formatPriceValue(fee.amount)}
                index={++this.index}
              />
            ))}
          </Accordion>
        )}
      </Accordion>
    );
  };

  render() {
    this.index = 0;

    const styles =
      this.props.user && IS_FORD_BRAND(this.props.user.brand)
        ? fordStyles
        : lincolnStyles;

    const data = this.props.pricingStack;
    const { customerReservation } = this.props;
    const isLincolnPurchaseRequest =
      this.props.isLincolnOrderDetails && this.props.isPurchaseRequest;

    const purchaseTypeIsSignAtDealer =
      customerReservation &&
      customerReservation.purchaseType === "SignAtDealer";

    const purchaseTypeIsSignOnline =
      customerReservation && customerReservation.purchaseType === "SignOnline";

    const financeProductType =
      customerReservation &&
      customerReservation.financeDataQuote &&
      customerReservation.financeDataQuote.financeProduct;

    const FINANCE = "Finance";
    const CASH = "Cash";
    const OTHER = "Other";
    const FORD_OPTIONS = "FordOptions";

    const showPreTaxPrice = !(
      this.props.isModelEOrder && purchaseTypeIsSignOnline
    );

    const showEstimatedTaxesAndFees = !(
      this.props.isModelEOrder &&
      (purchaseTypeIsSignAtDealer || purchaseTypeIsSignOnline) &&
      (financeProductType === FINANCE ||
        financeProductType === CASH ||
        financeProductType === OTHER ||
        financeProductType === FORD_OPTIONS)
    );

    const isOffline =
      customerReservation &&
      customerReservation.orderChannel &&
      customerReservation.orderChannel === "InStore" ? (
        <span className={styles.headerStatus}>
          {this.props.t(
            "OrderDetailsPricingSummary.offlineOrderPricingMessage"
          )}
        </span>
      ) : null;

    const pricingRows = (
      <>
        <Grid container justifyContent="space-between">
          <Grid item>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {this.props.t("OrderDetailsPricingSummary.totalDealerPrice")}
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="h5" sx={{ mb: 2 }}>
              {this.formatPricingDisplay(data?.totalDealerPrice)}
            </Typography>
          </Grid>
        </Grid>

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.baseMSRP")}
          indent
          data={this.formatPricingDisplay(data?.baseMSRP)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.totalOptions")}
          indent
          data={this.formatPricingDisplay(data?.totalOptions)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t(
            "OrderDetailsPricingSummary.destinationDeliveryCharges"
          )}
          indent
          data={this.formatPricingDisplay(data?.destinationDeliveryCharges)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.dealerAdjustment")}
          indent
          data={this.formatPricingDisplay(data?.dealerAdjustmentPrice)}
          index={++this.index}
        />

        {data && data.dealerSellingPriceDocFeeName && (
          <StyledTableRow
            text={data.dealerSellingPriceDocFeeName}
            indent
            data={this.formatPricingDisplay(data?.dealerSellingPriceDocFee)}
            index={++this.index}
          />
        )}

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.totalAddOnsPrice")}
          indent
          data={this.formatPricingDisplay(data?.totalAddOnsPrice)}
          index={++this.index}
        />

        {!isLincolnPurchaseRequest && (
          <StyledTableRow
            text={this.props.t("OrderDetailsPricingSummary.chargingPriceTotal")}
            data={this.formatPricingDisplay(data?.chargingPriceTotal)}
            index={++this.index}
          />
        )}

        <StyledTableRow
          text={this.props.t(
            "OrderDetailsPricingSummary.accessoriesPriceTotal"
          )}
          data={this.formatPricingDisplay(data?.accessoriesPriceTotal)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.protectionPlanTotal")}
          data={this.formatPricingDisplay(data?.protectionPlanTotal)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.purchaseDeposit")}
          data={this.formatPricingDisplay(data?.reservationDepositPrice)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.downPayment")}
          data={this.formatPricingDisplay(data?.downPaymentAmount)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t(
            "OrderDetailsPricingSummary.estimatedNetTradeInAmount"
          )}
          data={this.formatPricingDisplay(data?.tradeInPrice)}
          index={++this.index}
        />

        <StyledTableRow
          text={this.props.t("OrderDetailsPricingSummary.totalIncentivesPrice")}
          data={this.formatPricingDisplay(data?.totalIncentivesPrice)}
          index={++this.index}
        />

        {showPreTaxPrice && (
          <StyledTableRow
            text={this.props.t("OrderDetailsPricingSummary.totalPreTaxPrice")}
            data={this.formatPricingDisplay(data?.totalPreTaxPrice)}
            index={++this.index}
          />
        )}

        {data &&
          data.taxesAndFees &&
          showEstimatedTaxesAndFees &&
          this.renderTaxesAndFees(
            data.taxesAndFees,
            this.props.t("OrderDetailsPricingSummary.totalEstimatedTaxAndFees"),
            "taxesAndFeesExpansion"
          )}

        {!isLincolnPurchaseRequest &&
          data &&
          data.federalTaxCreditAmount !== null &&
          data.federalTaxCreditAmount !== undefined && (
            <StyledTableRow
              text={this.props.t("OrderDetailsPricingSummary.federalTaxCredit")}
              data={formatPriceValue(data.federalTaxCreditAmount)}
              index={++this.index}
            />
          )}

        <Box
          data-testid="pricing-summary-total"
          sx={{
            borderTop: this.props.isLincolnOrderDetails
              ? "none"
              : "2px solid black",
            mt: 4,
            pt: this.props.isLincolnOrderDetails ? 0 : 4,
          }}
        >
          <StyledBigTableHeader
            text={this.props.t(
              "OrderDetailsPricingSummary.estimatedTotalAmount"
            )}
            data={this.formatPricingDisplay(data?.totalPriceWithAddOns)}
          />
        </Box>
      </>
    );

    if (this.props.isLincolnOrderDetails) {
      return (
        <Box component="section" data-testid="lincoln-pricing-summary">
          <Typography
            variant="h3"
            sx={{
              mb: 6,
              fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "32px",
            }}
          >
            {this.props.t("pricing.summary.heading")}
          </Typography>
          {isOffline && <Box sx={{ mb: 4 }}>{isOffline}</Box>}
          <Box
            data-testid="lincoln-pricing-summary-values"
            sx={{
              width: { xs: "100%", md: "50%" },
              fontFamily: "ProximaNovaRegular, Arial, sans-serif",
              fontSize: "16px",
              lineHeight: "24px",
              "& .MuiTypography-h5": {
                fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: "24px",
              },
              // Tax details remain expandable, without the MUI separator lines.
              "& .MuiAccordion-root": {
                backgroundColor: "transparent",
                boxShadow: "none",
                "&::before": { display: "none" },
              },
              "& .MuiAccordionSummary-root": { px: 0 },
              "& .MuiAccordionSummary-content": { minWidth: 0 },
              "& .MuiGrid-container > .MuiGrid-item:first-of-type": {
                minWidth: 0,
                maxWidth: "75%",
                overflowWrap: "anywhere",
              },
              "& .MuiGrid-container > .MuiGrid-item:last-of-type": {
                color: "#102B4E",
                textAlign: "right",
              },
            }}
          >
            {pricingRows}
          </Box>
        </Box>
      );
    }

    return (
      <EMPAccordion
        heading={this.props.t("OrderDetailsPricingSummary.title")}
        subHeading={isOffline ? isOffline : ""}
        onChange={() => this.flipIcon()}
        expanded={this.state.pricingSummaryExpansion}
        data-testid="pricingDropDown"
        elevation={0}
      >
        {pricingRows}
      </EMPAccordion>
    );
  }
}

PricingSummary.propTypes = {
  isLincolnOrderDetails: PropTypes.bool,
  isPurchaseRequest: PropTypes.bool,
};

export default withTranslation("emp")(PricingSummary);
